import { useEffect, useRef, useState } from "react";
import {
  Briefcase,
  TrendingUp,
  DollarSign,
  Users,
  Target,
  Megaphone,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import TrialDialog from "@/components/TrialDialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";

type Feature = { title: string; desc: string };

type ModuleSection = {
  key: string;
  title: string;
  tagline: string;
  description: string;
  image: string;
  images?: string[];
  accent: string;
  Icon: React.ComponentType<{ className?: string }>;
  features: Feature[];
};

const modules: ModuleSection[] = [
  {
    key: "projects",
    title: "Projects",
    tagline: "AI-guided project delivery",
    description:
      "Plan, schedule and execute projects with AI assistance — from project templates and Gantt-based scheduling to real-time resource and cost orchestration.",
    image: "/lovable-uploads/Projects_1.jpg",
    images: [
      "/lovable-uploads/Projects_1.jpg",
      "/lovable-uploads/Projects_2.jpg",
      "/lovable-uploads/Projects_3.jpg",
    ],
    accent: "from-indigo-500/40 via-purple-500/30 to-fuchsia-500/40",
    Icon: Briefcase,
    features: [
      { title: "Smart Project Planning", desc: "AI templates and task breakdowns to launch projects faster." },
      { title: "Interactive Gantt Charts", desc: "Schedule, drag, and reflow tasks with cascading date logic." },
      { title: "Resource & Cost Control", desc: "Assign people, track utilization and blended cost in real time." },
      { title: "Kanban Boards", desc: "Organize subtasks into customizable workflow steps with drag-and-drop kanban." },
    ],
  },
  {
    key: "performance",
    title: "Performance",
    tagline: "Goals, appraisals & growth",
    description:
      "Run the full performance cycle — set goals, calibrate appraisals, band performance and craft improvement plans with AI-powered insights.",
    image: "/lovable-uploads/Performance_1.jpg",
    images: [
      "/lovable-uploads/Performance_1.jpg",
      "/lovable-uploads/Performance_2.jpg",
      "/lovable-uploads/Performance_3.jpg",
    ],
    accent: "from-emerald-500/40 via-teal-500/30 to-cyan-500/40",
    Icon: TrendingUp,
    features: [
      { title: "Goal Setting", desc: "Cascade objectives from company to team to individual." },
      { title: "AI-assisted Appraisals", desc: "Balanced reviews drawing from evidence across the year." },
      { title: "Performance Banding", desc: "Calibrate fairly with transparent banding workflows." },
      { title: "Improvement Plans", desc: "Targeted coaching and development with measurable milestones." },
    ],
  },
  {
    key: "finance",
    title: "Finance",
    tagline: "Sales finance, decoded",
    description:
      "Get AI-empowered visibility into sales performance, forecast accuracy, margin and gap analysis — and build credible bridge plans to close the gap.",
    image: "/lovable-uploads/Finance_1.jpg",
    images: [
      "/lovable-uploads/Finance_1.jpg",
      "/lovable-uploads/Finance_2.jpg",
      "/lovable-uploads/Finance_3.jpg",
    ],
    accent: "from-amber-500/40 via-orange-500/30 to-rose-500/40",
    Icon: DollarSign,
    features: [
      { title: "Forecast Accuracy", desc: "Track forecast vs. actuals with intelligent trend signals." },
      { title: "Gap & Margin Analysis", desc: "Pinpoint revenue and margin gaps across the funnel." },
      { title: "Bridge Plans", desc: "Build, edit, and track recovery actions to plan." },
      { title: "AI Analysis", desc: "Narrative insights on what's moving and what to do next." },
    ],
  },
  {
    key: "hr",
    title: "HR",
    tagline: "Talent acquisition on autopilot",
    description:
      "Streamline requirements, design org structure, define roles and discover the best candidates — internally and externally — with AI automation.",
    image: "/lovable-uploads/HR_1.jpg",
    images: [
      "/lovable-uploads/HR_1.jpg",
      "/lovable-uploads/HR_2.jpg",
      "/lovable-uploads/HR_3.jpg",
    ],
    accent: "from-sky-500/40 via-blue-500/30 to-indigo-500/40",
    Icon: Users,
    features: [
      { title: "Define Requirements", desc: "Capture role needs and translate them into job criteria." },
      { title: "Org Structure Editor", desc: "Visually design teams, roles and reporting lines." },
      { title: "AI Job Descriptions", desc: "Generate and refine JDs aligned to your standards." },
      { title: "Candidate Discovery", desc: "Find internal and external candidates with smart ranking." },
    ],
  },
  {
    key: "sales",
    title: "Sales",
    tagline: "Account Business Planning, supercharged",
    description:
      "Build winning Account Business Plans with deep customer insights, stakeholder maps, business value frameworks and orchestrated engagements.",
    image: "/lovable-uploads/Sales_1.jpg",
    images: [
      "/lovable-uploads/Sales_1.jpg",
      "/lovable-uploads/Sales_2.jpg",
      "/lovable-uploads/Sales_3.jpg",
    ],
    accent: "from-fuchsia-500/40 via-pink-500/30 to-rose-500/40",
    Icon: Target,
    features: [
      { title: "Customer KPIs", desc: "Real-time financial and operational KPIs per account." },
      { title: "Customer Org Chart", desc: "Map stakeholders, influence and relationship strength." },
      { title: "Business Value Framework", desc: "Tie your offer to measurable customer outcomes." },
      { title: "Orchestration & Key Asks", desc: "Coordinate virtual teams around the next best action." },
    ],
  },
  {
    key: "marketing",
    title: "Marketing",
    tagline: "GTM across the 4Ps",
    description:
      "Drive marketing GTM with AI-assisted budgeting, content creation, real-time sentiment analysis, and execution across Product, Price, Place and Promotion.",
    image: "/lovable-uploads/Marketing_1.jpg",
    images: [
      "/lovable-uploads/Marketing_1.jpg",
      "/lovable-uploads/Marketing_2.jpg",
      "/lovable-uploads/Marketing_3.jpg",
    ],
    accent: "from-violet-500/40 via-purple-500/30 to-indigo-500/40",
    Icon: Megaphone,
    features: [
      { title: "Budgeting", desc: "Plan and reallocate marketing spend with AI guidance." },
      { title: "Sentiment Analysis", desc: "Localized, real-time signals from across the web." },
      { title: "Content Creation", desc: "Generate on-brand content for every channel." },
      { title: "4Ps Orchestration", desc: "Coordinate Product, Price, Place and Promotion in one place." },
    ],
  },
];

const ModuleImageCarousel = ({ images, title }: { images: string[]; title: string }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), 4000);
    return () => clearInterval(t);
  }, [images.length]);
  return (
    <div className="relative w-full h-[360px] md:h-[420px] bg-slate-950">
      {images.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={`${title} screenshot ${i + 1}`}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-700 ${
            i === idx ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
      <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2 z-10">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`Show slide ${i + 1}`}
            className={`w-2 h-2 rounded-full transition-all ${
              i === idx ? "bg-primary scale-125 shadow-[0_0_8px_rgba(155,135,245,0.9)]" : "bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const ModuleSectionView = ({
  mod,
  index,
  onExplore,
  onWatchProjectsDemo,
}: {
  mod: ModuleSection;
  index: number;
  onExplore: () => void;
  onWatchProjectsDemo?: () => void;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const reversed = index % 2 === 1;
  const { Icon } = mod;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setVisible(true);
        });
      },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id={`module-${mod.key}`}
      className="relative py-8 scroll-mt-24"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {/* Image */}
          <div className={`${reversed ? "lg:order-2" : ""}`}>
            <div className="relative group">
              <div
                className={`absolute -inset-1 rounded-3xl bg-gradient-to-br ${mod.accent} blur-2xl opacity-60 group-hover:opacity-90 transition-opacity duration-500`}
              />
              <div className="relative rounded-3xl overflow-hidden border border-white/15 bg-slate-950/60 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]">
                {mod.images && mod.images.length > 1 ? (
                  <ModuleImageCarousel images={mod.images} title={mod.title} />
                ) : (
                  <img
                    src={mod.image}
                    alt={`${mod.title} module`}
                    loading="lazy"
                    className="w-full h-[360px] md:h-[420px] object-cover object-center group-hover:scale-[1.03] transition-transform duration-700"
                  />
                )}
                {!mod.images && (
                  <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/60 via-slate-900/10 to-transparent pointer-events-none" />
                )}
                <div className="absolute top-4 right-4 flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-3 py-1 text-xs text-white/90 z-10">
                  <Sparkles className="w-3.5 h-3.5 text-primary" />
                  AI-powered
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className={`${reversed ? "lg:order-1" : ""}`}>
            <div className="flex items-center gap-3 mb-4">
              <div
                className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${mod.accent} border border-white/15 flex items-center justify-center shadow-lg`}
              >
                <Icon className="w-6 h-6 text-white" />
              </div>
              <span className="uppercase tracking-[0.2em] text-xs text-primary/80">
                {mod.tagline}
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {mod.title}{" "}
              <span className="bg-gradient-to-r from-primary to-fuchsia-400 bg-clip-text text-transparent">
                module
              </span>
            </h2>
            <p className="text-indigo-100/80 text-lg leading-relaxed mb-8">
              {mod.description}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {mod.features.map((f) => (
                <div
                  key={f.title}
                  className="group relative rounded-xl p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 backdrop-blur-md transition-all duration-300"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-1 w-2 h-2 rounded-full bg-primary shadow-[0_0_10px_rgba(155,135,245,0.9)]" />
                    <div>
                      <h4 className="text-white font-semibold mb-1">{f.title}</h4>
                      <p className="text-indigo-100/70 text-sm leading-snug">{f.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={onExplore}
                className="group inline-flex items-center gap-2 text-primary/90 hover:text-primary text-sm transition-colors"
              >
                <span>Explore the {mod.title} module</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              {mod.key === "projects" && onWatchProjectsDemo && (
                <Button
                  className="bg-secondary text-white hover:bg-white w-[180px] hover:text-black border border-white/20"
                  size="lg"
                  onClick={onWatchProjectsDemo}
                >
                  Watch Projects demo
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
    </section>
  );
};

const ModulesShowcase = ({ autoOpenProjectsDemo = false }: { autoOpenProjectsDemo?: boolean }) => {
  const [trialOpen, setTrialOpen] = useState(false);
  const [showProjectsDemo, setShowProjectsDemo] = useState(autoOpenProjectsDemo);
  useEffect(() => {
    if (autoOpenProjectsDemo) setShowProjectsDemo(true);
  }, [autoOpenProjectsDemo]);
  return (
    <div className="relative mt-8">
      <div className="max-w-6xl mx-auto px-6 text-center mb-4">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-md text-xs uppercase tracking-[0.25em] text-primary/90 mb-6">
          <Sparkles className="w-3.5 h-3.5" />
          One platform, eight modules
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
          Built to{" "}
          <span className="bg-gradient-to-r from-primary via-fuchsia-400 to-indigo-300 bg-clip-text text-transparent">
            empower every function
          </span>
        </h2>
        <p className="text-indigo-100/80 text-lg max-w-3xl mx-auto">
          From projects to people, finance to marketing — Ingant unifies your business
          with AI that augments human decisions across the enterprise.
        </p>
        <p></p>
        <p></p>
      </div>

      {modules.map((mod, i) => (
        <ModuleSectionView
          key={mod.key}
          mod={mod}
          index={i}
          onExplore={() => setTrialOpen(true)}
          onWatchProjectsDemo={() => setShowProjectsDemo(true)}
        />
      ))}

      <TrialDialog open={trialOpen} onOpenChange={setTrialOpen} />

      <Dialog open={showProjectsDemo} onOpenChange={setShowProjectsDemo}>
        <DialogContent className="sm:max-w-[80%] p-0 border-none bg-transparent">
          <div style={{ position: 'relative', overflow: 'hidden', aspectRatio: '1920/1080' }}>
            <iframe
              src="https://share.synthesia.io/embeds/videos/7ae67338-353e-4ef6-ae85-cd7ba72b709c"
              loading="lazy"
              title="Synthesia video player - Ingant Projects demo video"
              allowFullScreen
              allow="encrypted-media; fullscreen;"
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                border: 'none',
                padding: 0,
                margin: 0,
                overflow: 'hidden'
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModulesShowcase;
